import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Spinner, ButtonGroup, Button } from "react-bootstrap";
import cronstrue from "cronstrue";
import { useSelector, useDispatch } from "react-redux";

// VM imports
import { getAllAWSVms } from "../features/vm/getAllAWSVmsSlice";
import { updateAWSVmState } from "../features/vm/updateAWSVmStateSlice";
import { getAWSVmStatus } from "../features/vm/getAWSVmStatusSlice";

// DB imports - CORRECTED PATHS
import { getAllAWSDBs } from "../features/db/getAllAWSDBsSlice";
import { updateAWSDBState } from "../features/db/updateAWSDBStateSlice";
import { getAWSDBStatus } from "../features/db/getAWSDBStatusSlice";
import { getAWSDBLogs } from "../features/db/getAWSDbLogsSlice";
import { updateAWSDBSchedule } from "../features/db/updateAWSDbScheduleSlice";

import { getAWSVmLogs } from "../features/vm/getAWSVmLogsSlice";
import { updateAWSVmSchedule } from "../features/vm/updateAWSVmScheduleSlice";
import { deleteCron } from "../features/deleteCronSlice";

// Reusable Components
import AccountAccordion from "../reusable-components/AccountAccordion";
import SummaryBar from "../reusable-components/SummaryBar";
import ToastNotification from "../reusable-components/ToastNotification";
import ScheduleModal from "../reusable-components/ScheduleModal";
import VmTable from "../reusable-components/VmTable";

function ListAllAWSAccount() {
  const dispatch = useDispatch();
  const { loading, loginUser } = useSelector((state) => state.user);
  const { resources: vmResources } = useSelector((state) => state.getAllAWSVms);
  const { resources: dbResources } = useSelector((state) => state.getAllAWSDBs);

  // Toggle state for resource type
  const [resourceType, setResourceType] = useState("vms"); // "vms" or "dbs"

  const aws_accounts = useMemo(() => loginUser?.perms?.AWS || [], [loginUser]);
  const initialAccounts = useMemo(
    () =>
      aws_accounts.map((acc) => ({
        name: acc.account_name,
        id: acc.account_id,
        env: acc.app_tags,
        vms: [],
        dbs: [],
      })),
    [aws_accounts],
  );

  const [accounts, setAccounts] = useState(initialAccounts);
  const [pagination, setPagination] = useState(() =>
    initialAccounts.reduce((acc, _, idx) => {
      acc[idx] = { page: 1, perPage: 5 };
      return acc;
    }, {}),
  );

  const [paginatedResourcesByAccount, setPaginatedResourcesByAccount] =
    useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({
    accIdx: null,
    resourceIdx: null,
    field: "",
  });
  const [cronInputs, setCronInputs] = useState({
    time: "08:00",
    timezone: "UTC",
    frequency: "daily",
    days: [],
  });

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const [errorAccounts, setErrorAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState([]);
  const [activeKeys, setActiveKeys] = useState([]);
  const [loadedVmAccounts, setLoadedVmAccounts] = useState([]);
  const [loadedDbAccounts, setLoadedDbAccounts] = useState([]);

  // Get current resources based on type - memoized with useCallback
  const getCurrentResources = useCallback(
    (account) => {
      return resourceType === "vms" ? account.vms : account.dbs;
    },
    [resourceType],
  );

  // --- Sync pagination with accounts ---

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState({});

  const [accountPage, setAccountPage] = useState(1);
  const accountsPerPage = 5;
  const totalAccountPages = Math.ceil(accounts.length / accountsPerPage) || 1;

  // Loading state for schedule save/remove operations
  const [scheduleLoading, setScheduleLoading] = useState({});
  const paginatedAccounts = accounts.slice(
    (accountPage - 1) * accountsPerPage,
    accountPage * accountsPerPage,
  );

  // NEW: track which accordion is open across pagination
  const [openAccordionIndex, setOpenAccordionIndex] = useState(null);

  useEffect(() => {
    setPagination((prev) => {
      const newPag = {};
      accounts.forEach((_, idx) => {
        newPag[idx] = prev[idx] || { page: 1, perPage: 5 };
      });
      return newPag;
    });
  }, [accounts]);

  useEffect(() => {
    const newPaginated = {};

    accounts.forEach((acc, accIdx) => {
      const resources = getCurrentResources(acc);

      const { page, perPage } = pagination[accIdx] || { page: 1, perPage: 5 };
      newPaginated[accIdx] = resources.slice(
        (page - 1) * perPage,
        page * perPage,
      );
    });

    setPaginatedResourcesByAccount(newPaginated);
  }, [accounts, pagination, resourceType, getCurrentResources]);

  useEffect(() => {
    if (vmResources && vmResources.vms) {
      setAccounts((prev) => {
        const updated = [...prev];
        const apiAccountId = vmResources.accountId || vmResources.id;
        const accIndex = updated.findIndex((acc) => acc.id === apiAccountId);
        if (accIndex !== -1) {
          updated[accIndex].vms = vmResources.vms.map((vm) => ({
            name: vm.vmName,
            tag: vm.vmTag,
            type:'-',
            status: vm.vmStatus.charAt(0).toUpperCase() + vm.vmStatus.slice(1),
            id: vm.vmId,
            startSchedule: vm.vmCronStart || "",
            stopSchedule: vm.vmCronStop || "",
            startScheduleTimezone: vm.vmCronStartTimezone || "UTC",
            stopScheduleTimezone: vm.vmCronStopTimezone || "UTC",
            disableActions: false,
            isLoading: false,
          }));
        }
        return updated;
      });
    }
  }, [vmResources]);

  // --- DB Resource Effect ---
  useEffect(() => {
    if (dbResources && dbResources.dbs) {
      setAccounts((prev) => {
        const updated = [...prev];
        const apiAccountId = dbResources.accountId || dbResources.id;
        const accIndex = updated.findIndex((acc) => acc.id === apiAccountId);
        if (accIndex !== -1) {
          updated[accIndex].dbs = dbResources.dbs.map((db) => ({
            name: db.dbName,
            tag: db.dbTag,
            type: db.dbType,
            status: db.dbStatus.charAt(0).toUpperCase() + db.dbStatus.slice(1),
            id: db.dbId, // Using vmId for consistency with VmTable component
            startSchedule: db.dbCronStart || "",
            stopSchedule: db.dbCronStop || "",
            startScheduleTimezone: db.dbCronStartTimezone || "UTC",
            stopScheduleTimezone: db.dbCronStopTimezone || "UTC",
            disableActions: false,
            isLoading: false,
          }));
        }
        return updated;
      });
    }
  }, [dbResources]);

  useEffect(() => {
    setAccounts(initialAccounts);
  }, [initialAccounts]);

  // --- Reset accordion state when switching between VMs and DBs ---
  useEffect(() => {
    setActiveKeys([]);
    setErrorAccounts([]);
    setLoadingAccounts([]);
  }, [resourceType]);

  // --- Style Helpers ---
  const getRowStyle = (status) => {
    if (!status) return {};
    const s = status.toLowerCase();
    if (s === "running") return { backgroundColor: "#e6f4ea" };
    if (s === "stopped") return { backgroundColor: "#fae3e3" };
    if (status === "Starting Soon") return { backgroundColor: "#fff7d6" };
    return {};
  };
  const getBadgeVariant = (status) => {
    if (!status) return "secondary";
    const s = status.toLowerCase();
    if (s === "running") return "success";
    if (s === "stopped") return "danger";
    if (status === "Starting Soon") return "warning";
    return "secondary";
  };

  const pollResourceStatus = async (
    accountId,
    resourceId,
    accIdx,
    resourceIdx,
    resourceTag,
    targetStatus,
    dbType
  ) => {
    const startTime = Date.now();
    const maxDuration = 3 * 60 * 1000; // 2 minutes
    const resourceKey = resourceType === "vms" ? "vms" : "dbs";
    const statusAction =
      resourceType === "vms" ? getAWSVmStatus : getAWSDBStatus;
    // const idKey = resourceType === "vms" ? "vmId" : "dbId";
    // const tagKey = resourceType === "vms" ? "vmTag" : "dbTag";

    const pollInterval = setInterval(async () => {
      const elapsed = Date.now() - startTime;
      try {
        var response 
        if(resourceType === "vms"){
          response = await dispatch(
              statusAction({
                accountId,
                id: resourceId,
                tag: resourceTag,
              }),
            ).unwrap();
          }
          else if(resourceType !== "vms"){
             response = await dispatch(
              statusAction({
                accountId,
                id: resourceId,
                tag: resourceTag,
                type:dbType
              }),
            ).unwrap();
          }

        const statusKey = resourceType === "vms" ? "vmStatus" : "dbStatus";
        if (response?.[resourceKey][0]?.[statusKey]) {
          const updatedStatus =
            response[resourceKey][0][statusKey].charAt(0).toUpperCase() +
            response[resourceKey][0][statusKey].slice(1);

          if (updatedStatus === targetStatus) {
            setAccounts((prev) => {
              const updated = [...prev];
              const resource = updated[accIdx][resourceKey][resourceIdx];

              updated[accIdx][resourceKey][resourceIdx] = {
                ...resource,
                status: updatedStatus,
                isLoading: updatedStatus !== targetStatus,
                disableActions: updatedStatus !== targetStatus,
              };

              if (!updated[accIdx][resourceKey][resourceIdx].isLoading) {
                clearInterval(pollInterval);
                updated[accIdx][resourceKey][resourceIdx].disableActions =
                  false;
              }

              if (elapsed >= maxDuration) {
                clearInterval(pollInterval);
                updated[accIdx][resourceKey][resourceIdx].isLoading = false;
                updated[accIdx][resourceKey][resourceIdx].disableActions =
                  false;
                setToastMessage(
                  `${resourceType === "vms" ? "VM" : "Database"} "${
                    resource.name
                  }" did not reach ${targetStatus} within 2 minutes.`,
                );
                setShowToast(true);
              }

              return updated;
            });
            clearInterval(pollInterval);
          }
        }
      } catch {
        clearInterval(pollInterval);
        setAccounts((prev) => {
          const updated = [...prev];
          updated[accIdx][resourceKey][resourceIdx].isLoading = false;
          updated[accIdx][resourceKey][resourceIdx].disableActions = false;
          return updated;
        });
      }
    }, 30000);
  };

  const handleStart = (accIdx, resourceIdx) => {
    const resourceKey = resourceType === "vms" ? "vms" : "dbs";
    // const idKey = resourceType === "vms" ? "vmId" : "dbId";
    const accountId = accounts[accIdx].id;
    const resourceId = accounts[accIdx][resourceKey][resourceIdx].id;
    const resourceTag = accounts[accIdx][resourceKey][resourceIdx].tag;
    const updateAction =
      resourceType === "vms" ? updateAWSVmState : updateAWSDBState;

    setAccounts((prev) => {
      const updated = [...prev];
      updated[accIdx][resourceKey][resourceIdx] = {
        ...updated[accIdx][resourceKey][resourceIdx],
        status: "Starting Soon",
        disableActions: true,
        isLoading: true,
      };
      return updated;
    });
    if(resourceType === "vms"){
    dispatch(
      updateAction({ accountId, resourceId: resourceId, action: "start" }),
    )
      .unwrap()
      .then(() =>
        pollResourceStatus(
          accountId,
          resourceId,
          accIdx,
          resourceIdx,
          resourceTag,
          "Running",
          ""
        ),
      );
    }
      else if(resourceType !== "vms"){
        const dbType = accounts[accIdx][resourceKey][resourceIdx].type;
        dispatch(
          updateAction({ accountId, resourceId: resourceId, dbType:dbType,action: "start" }),
        )
          .unwrap()
          .then(() =>
            pollResourceStatus(
              accountId,
              resourceId,
              accIdx,
              resourceIdx,
              resourceTag,
              "Available",
              dbType
            ),
          );
      }
  };

  const handleStop = (accIdx, resourceIdx) => {
    const resourceKey = resourceType === "vms" ? "vms" : "dbs";
    //const idKey = resourceType === "vms" ? "vmId" : "dbId";
    const accountId = accounts[accIdx].id;
    const resourceId = accounts[accIdx][resourceKey][resourceIdx].id;
    const resourceTag = accounts[accIdx][resourceKey][resourceIdx].tag;
    const updateAction =
      resourceType === "vms" ? updateAWSVmState : updateAWSDBState;

    setAccounts((prev) => {
      const updated = [...prev];
      updated[accIdx][resourceKey][resourceIdx] = {
        ...updated[accIdx][resourceKey][resourceIdx],
        status: "Stopping",
        disableActions: true,
        isLoading: true,
      };
      return updated;
    });
    if(resourceType === "vms"){
    dispatch(
      updateAction({ accountId, resourceId: resourceId, action: "stop" }),
    )
      .unwrap()
      .then(() =>
        pollResourceStatus(
          accountId,
          resourceId,
          accIdx,
          resourceIdx,
          resourceTag,
          "Stopped",
          ""
        ),
      );
    }
      else if(resourceType !== "vms"){
        const dbType = accounts[accIdx][resourceKey][resourceIdx].type;
        dispatch(
          updateAction({ accountId, resourceId: resourceId, dbType:dbType,action: "stop" }),
        )
          .unwrap()
          .then(() =>
            pollResourceStatus(
              accountId,
              resourceId,
              accIdx,
              resourceIdx,
              resourceTag,
              "Stopped",
              dbType
            ),
          );
      }
  };

  const handleInfoClick = async (accIdx, resourceIdx) => {
    const resourceKey = resourceType === "vms" ? "vms" : "dbs";
    // const idKey = resourceType === "vms" ? "vmId" : "dbId";
    const accountId = accounts[accIdx].id;
    const resourceId = accounts[accIdx][resourceKey][resourceIdx].id;
    const resourceTag = accounts[accIdx][resourceKey][resourceIdx].tag;
    const logAction = resourceType === "vms" ? getAWSVmLogs : getAWSDBLogs;
    const loadingKey = `${accIdx}-${resourceIdx}`;
    setLoadingHistory((prev) => ({ ...prev, [loadingKey]: true }));
    setShowHistoryModal(true);
    try {
      const resultAction = await dispatch(
        logAction({ accountId, instanceId: resourceId, tag: resourceTag }),
      );
      if (logAction.fulfilled.match(resultAction)) {
        const apiData = resultAction.payload;
        const formattedData =
          apiData.events?.map((ev) => ({
            user: ev.username,
            action: ev.eventName,
            time: new Date(ev.eventTime).toLocaleString(),
          })) || [];
        setHistoryData(formattedData);
      } else {
        setHistoryData([]);
      }
    } catch {
      setHistoryData([]);
    } finally {
      setLoadingHistory((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const getCronTooltipContent = (cronExpression) => {
    try {
      if (!cronExpression) return "No cron set";
      return cronstrue.toString(cronExpression);
    } catch {
      return "Invalid cron format";
    }
  };

  // Compact summary for table cell (e.g., "08:00 Mon, Tue, Wed (UTC)")
  const getCronSummary = (cronExpression, timezone) => {
    try {
      if (!cronExpression) return "—";
      const parts = cronExpression.trim().split(/\s+/);
      if (parts.length < 5) return cronExpression;

      const minute = parts[0].padStart(2, "0");
      const hour = parts[1].padStart(2, "0");
      const time = `${hour}:${minute}`;

      const dayOfWeekPart = parts[4];
      let daysLabel = "";

      if (dayOfWeekPart !== "*" && dayOfWeekPart !== "?") {
        const map = {
          SUN: "Sun",
          MON: "Mon",
          TUE: "Tue",
          WED: "Wed",
          THU: "Thu",
          FRI: "Fri",
          SAT: "Sat",
        };
        const days = dayOfWeekPart
          .split(",")
          .map((d) => map[d.toUpperCase()] || d)
          .join(", ");
        daysLabel = ` ${days}`;
      }

      const tz = timezone ? ` (${timezone})` : "";
      return `${time}${daysLabel}${tz}`;
    } catch {
      return cronExpression;
    }
  };

  const saveCronExpression = () => {
    const [hour, minute] = cronInputs.time.split(":").map(Number);
    let cronExp = "";

    if (cronInputs.frequency === "daily") {
      // AWS format: min hour day-of-month month day-of-week year
      cronExp = `${minute} ${hour} * * ? *`;
    } else if (cronInputs.frequency === "weekly") {
      const mapping = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
      const daysArr = cronInputs.days
        .filter((d) => d !== null && d !== undefined) // Remove invalid entries
        .map((d) => mapping[d]); // Map numbers to names

      if (daysArr.length > 0) {
        cronExp = `${minute} ${hour} ? * ${daysArr.join(",")} *`;
      } else {
        //alert("Please select at least one day for a weekly schedule.");
        setToastMessage(
          "Please select at least one day for a weekly schedule.",
        );
        setShowToast(true);
        return;
      }
    }
    const resourceKey = resourceType === "vms" ? "vms" : "dbs";
    const timezoneField =
      modalData.field === "startSchedule"
        ? "startScheduleTimezone"
        : "stopScheduleTimezone";
    const timezoneAbbr = cronInputs.timezone || "UTC";
    setAccounts((prev) => {
      const updated = [...prev];
      const resource =
        updated[modalData.accIdx][resourceKey][modalData.resourceIdx];
      updated[modalData.accIdx][resourceKey][modalData.resourceIdx] = {
        ...resource,
        [modalData.field]: cronExp, // dynamically update startSchedule or stopSchedule
        [timezoneField]: timezoneAbbr, // store timezone
      };
      return updated;
    });

    // const idKey = resourceType === "vms" ? "vmId" : "dbId";
    const accountId = accounts[modalData.accIdx].id;
    const resourceId =
      accounts[modalData.accIdx][resourceKey][modalData.resourceIdx].id;
    const resourceTag =
      accounts[modalData.accIdx][resourceKey][modalData.resourceIdx].tag;
    const scheduleAction =
      resourceType === "vms" ? updateAWSVmSchedule : updateAWSDBSchedule;

    const payload = {
      account: accountId,
      tag: resourceTag,
      resource: resourceId,
      type: resourceType === "vms" ? "ec2" : "rds",
      cron_expression: cronExp,
      timezone: timezoneAbbr, // 3-letter abbreviation (UTC, IST, EST, etc.)
      action: modalData.field === "startSchedule" ? "start" : "stop",
    };
    if (resourceType !== "vms") {
      const dbType =
        accounts[modalData.accIdx][resourceKey][modalData.resourceIdx].type;
      payload.db_type = dbType; 
    }

    const loadingKey = `${modalData.accIdx}-${modalData.resourceIdx}-${modalData.field}`;
    setScheduleLoading((prev) => ({ ...prev, [loadingKey]: true }));
    setShowModal(false);

    dispatch(scheduleAction(payload))
      .unwrap()
      .then(() => {
        setToastMessage("Schedule updated successfully!");
        setShowToast(true);
      })
      .catch(() => {
        setToastMessage("Failed to update schedule!");
        setShowToast(true);
      })
      .finally(() => {
        setScheduleLoading((prev) => ({ ...prev, [loadingKey]: false }));
      });
  };

  const resetCronExpression = () => {
    const resourceKey = resourceType === "vms" ? "vms" : "dbs";

    // Get resource details
    const accountId = accounts[modalData.accIdx].id;
    const resourceId =
      accounts[modalData.accIdx][resourceKey][modalData.resourceIdx].id;
    const resourceTag =
      accounts[modalData.accIdx][resourceKey][modalData.resourceIdx].tag;

    // Update local state: remove cron
    const timezoneField =
      modalData.field === "startSchedule"
        ? "startScheduleTimezone"
        : "stopScheduleTimezone";
    setAccounts((prev) => {
      const updated = [...prev];
      const resource =
        updated[modalData.accIdx][resourceKey][modalData.resourceIdx];
      updated[modalData.accIdx][resourceKey][modalData.resourceIdx] = {
        ...resource,
        [modalData.field]: "", // clear startSchedule or stopSchedule
        [timezoneField]: "", // clear timezone
      };
      return updated;
    });

    // Prepare deletion payload
    const payload = {
      account: accountId,
      tag: resourceTag,
      resource: resourceId,
      action: modalData.field === "startSchedule" ? "start" : "stop",
    };
    if (resourceType !== "vms") {
      const dbType =
        accounts[modalData.accIdx][resourceKey][modalData.resourceIdx].type;
      payload.db_type = dbType; 
    }

    // Call DELETE API for cron
    const loadingKey = `${modalData.accIdx}-${modalData.resourceIdx}-${modalData.field}`;
    setScheduleLoading((prev) => ({ ...prev, [loadingKey]: true }));

    dispatch(deleteCron(payload))
      .unwrap()
      .then(() => {
        setToastMessage("Schedule deleted successfully.");
        setShowToast(true);
      })
      .catch((err) => {
        console.error("Failed to delete schedule:", err);
        setToastMessage("Error while deleting schedule.");
        setShowToast(true);
      })
      .finally(() => {
        setScheduleLoading((prev) => ({ ...prev, [loadingKey]: false }));
      });
  };

  const handleItemsPerPageChange = (accIdx, value) => {
    setPagination((prev) => ({
      ...prev,
      [accIdx]: { ...prev[accIdx], perPage: Number(value), page: 1 },
    }));
  };

  const handlePageChange = (accIdx, newPage) => {
    setPagination((prev) => ({
      ...prev,
      [accIdx]: { ...prev[accIdx], page: newPage },
    }));
  };

  const openScheduleModal = (accIdx, resourceIdx, field, existingCron) => {
    setModalData({ accIdx, resourceIdx, field });
    setShowModal(true);
    const resourceKey = resourceType === "vms" ? "vms" : "dbs";
    const resource = accounts[accIdx][resourceKey][resourceIdx];
    const timezoneField =
      field === "startSchedule"
        ? "startScheduleTimezone"
        : "stopScheduleTimezone";
    const existingTimezone = resource[timezoneField] || "UTC";

    if (existingCron) {
      try {
        const parts = existingCron.trim().split(/\s+/); // split by spaces

        const minute = parts[0].padStart(2, "0");
        const hour = parts[1].padStart(2, "0");

        const dayOfWeekPart = parts.length === 6 ? parts[4] : parts[4];
        // Cron: minute hour dayOfMonth month dayOfWeek [year]

        let days = [];
        if (dayOfWeekPart !== "*" && dayOfWeekPart !== "?") {
          days = dayOfWeekPart.split(",").map((d) => {
            // Convert SUN/MON to number based on cron standard (SUN=0, MON=1...)
            const map = {
              SUN: 0,
              MON: 1,
              TUE: 2,
              WED: 3,
              THU: 4,
              FRI: 5,
              SAT: 6,
            };
            return isNaN(d) ? map[d.toUpperCase()] : Number(d);
          });
        }

        setCronInputs({
          time: `${hour}:${minute}`,
          timezone: existingTimezone,
          frequency:
            dayOfWeekPart === "*" || dayOfWeekPart === "?" ? "daily" : "weekly",
          days,
        });
      } catch (err) {
        console.error("Cron parse error:", err);
        setCronInputs({
          time: "08:00",
          timezone: existingTimezone,
          frequency: "daily",
          days: [],
        });
      }
    } else {
      setCronInputs({
        time: "08:00",
        timezone: existingTimezone,
        frequency: "daily",
        days: [],
      });
    }
  };

  // --- Accordion Click Handler with Error Handling ---
  const handleAccordionClick = (accIdx) => {
    const fetchAction = resourceType === "vms" ? getAllAWSVms : getAllAWSDBs;
    const loadedAccounts =
      resourceType === "vms" ? loadedVmAccounts : loadedDbAccounts;
    const setLoadedAccounts =
      resourceType === "vms" ? setLoadedVmAccounts : setLoadedDbAccounts;

    // Check if this account has already been loaded for the current resource type
    if (!loadedAccounts.includes(accIdx) && !loadingAccounts.includes(accIdx)) {
      setLoadingAccounts((prev) => [...prev, accIdx]);
      dispatch(fetchAction(accounts[accIdx].id))
        .unwrap()
        .then(() => {
          setErrorAccounts((prev) => prev.filter((idx) => idx !== accIdx));
          setLoadedAccounts((prev) => [...prev, accIdx]); // Mark as loaded
        })
        .catch(() =>
          setErrorAccounts((prev) =>
            prev.includes(accIdx) ? prev : [...prev, accIdx],
          ),
        )
        .finally(() =>
          setLoadingAccounts((prev) => prev.filter((idx) => idx !== accIdx)),
        );
    }
  };
  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <Spinner animation="border" role="status" />
        <span className="ms-2 text-muted">Loading AWS account details...</span>
      </div>
    );
  }
  return (
    <div className="p-3">
      <ToastNotification
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
      />

      {/* Toggle Button */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>AWS {resourceType === "vms" ? "Virtual Machines" : "Databases"}</h4>
        <ButtonGroup>
          <Button
            variant={resourceType === "vms" ? "primary" : "outline-primary"}
            onClick={() => setResourceType("vms")}
          >
            Virtual Machines
          </Button>
          <Button
            variant={resourceType === "dbs" ? "primary" : "outline-primary"}
            onClick={() => setResourceType("dbs")}
          >
            Databases
          </Button>
        </ButtonGroup>
      </div>

      <AccountAccordion
        accounts={paginatedAccounts}
        errorAccounts={errorAccounts
          .filter(
            (idx) =>
              idx >= (accountPage - 1) * accountsPerPage &&
              idx < accountPage * accountsPerPage,
          )
          .map((idx) => idx - (accountPage - 1) * accountsPerPage)}
        loadingAccounts={loadingAccounts
          .filter(
            (idx) =>
              idx >= (accountPage - 1) * accountsPerPage &&
              idx < accountPage * accountsPerPage,
          )
          .map((idx) => idx - (accountPage - 1) * accountsPerPage)}
        paginatedVmsByAccount={paginatedResourcesByAccount}
        pagination={pagination}
        activeKey={activeKeys}
        onSelect={setActiveKeys}
        openIndex={
          openAccordionIndex !== null &&
          openAccordionIndex >= (accountPage - 1) * accountsPerPage &&
          openAccordionIndex < accountPage * accountsPerPage
            ? openAccordionIndex - (accountPage - 1) * accountsPerPage
            : null
        }
        onAccordionClick={(accIdx) => {
          const actualIdx = accIdx + (accountPage - 1) * accountsPerPage;
          setOpenAccordionIndex(
            (prevOpen) => (prevOpen === actualIdx ? null : actualIdx), // toggle open/close
          );
          handleAccordionClick(actualIdx);
        }}
        renderBody={({
          account,
          accIdx,
          hasError,
          isLoading,
          page,
          perPage,
        }) => {
          const actualIdx = accIdx + (accountPage - 1) * accountsPerPage;
          const resourceKey = resourceType === "vms" ? "vms" : "dbs";
          const paginatedResources =
            paginatedResourcesByAccount[actualIdx] || [];
          const totalPages =
            Math.ceil(account[resourceKey].length / perPage) || 1;
          const accountSummary = account[resourceKey].reduce(
            (accSum, vm) => {
              if (vm.status === "Running") accSum.running++;
              else if (vm.status === "Stopped") accSum.stopped++;
              return accSum;
            },
            { running: 0, stopped: 0 },
          );
          return hasError ? (
            <div className="alert alert-danger py-2 mb-3" role="alert">
              Failed to load {resourceType === "vms" ? "VMs" : "databases"} for
              this account.
            </div>
          ) : (
            <div>
              <SummaryBar summary={accountSummary} />
              {isLoading && account[resourceKey].length === 0 ? (
                <div className="p-3 text-center">
                  <Spinner animation="border" role="status" />
                  <div className="mt-2 text-muted">
                    Loading {resourceType === "vms" ? "VMs" : "Databases"}...
                  </div>
                </div>
              ) : account[resourceKey].length === 0 ? (
                <div className="p-3 bg-white border text-center text-muted fw-bold">
                  No {resourceType === "vms" ? "VMs" : "databases"} available
                  for this account
                </div>
              ) : (
                <VmTable
                  vms={paginatedResources}
                  accIdx={actualIdx}
                  page={page}
                  perPage={perPage}
                  totalPages={totalPages}
                  onStart={(resourceIdx) => handleStart(actualIdx, resourceIdx)}
                  onStop={(resourceIdx) => handleStop(actualIdx, resourceIdx)}
                  onEditSchedule={(field, resourceIdx) =>
                    openScheduleModal(
                      actualIdx,
                      resourceIdx,
                      field,
                      paginatedResources[resourceIdx][field],
                    )
                  }
                  onInfoClick={(vmIdx) => handleInfoClick(actualIdx, vmIdx)}
                  getRowStyle={getRowStyle}
                  getBadgeVariant={getBadgeVariant}
                  getCronTooltipContent={getCronTooltipContent}
                  getCronSummary={getCronSummary}
                  onPageChange={(e) =>
                    handlePageChange(actualIdx, Number(e.target.value))
                  }
                  onPerPageChange={(e) =>
                    handleItemsPerPageChange(actualIdx, e.target.value)
                  }
                  loadingHistory={loadingHistory}
                  historyData={historyData}
                  showHistoryModal={showHistoryModal}
                  onHistoryClose={() => setShowHistoryModal(false)}
                  scheduleLoading={scheduleLoading}
                />
              )}
            </div>
          );
        }}
      />
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          Showing {(accountPage - 1) * accountsPerPage + 1} -{" "}
          {Math.min(accountPage * accountsPerPage, accounts.length)} of{" "}
          {accounts.length} accounts
        </div>
        <div>
          <button
            className="btn btn-outline-secondary btn-sm me-2"
            disabled={accountPage === 1}
            onClick={() => {
              setAccountPage(accountPage - 1);
              setActiveKeys([]);
              setOpenAccordionIndex(null);
            }}
          >
            Prev
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={accountPage === totalAccountPages}
            onClick={() => {
              setAccountPage(accountPage + 1);
              setActiveKeys([]);
              setOpenAccordionIndex(null);
            }}
          >
            Next
          </button>
        </div>
      </div>

      <ScheduleModal
        show={showModal}
        onHide={() => setShowModal(false)}
        cronInputs={cronInputs}
        setCronInputs={setCronInputs}
        onSave={saveCronExpression}
        resetCron={resetCronExpression}
      />
    </div>
  );
}

export default ListAllAWSAccount;
